import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Users, Search, Bot, Brain, LogOut, User, Shield, BarChart3, MessageSquare, Calendar, Workflow, TrendingUp, UserPlus, Zap, FileText, Phone, Globe, Link2, ChevronDown, Settings, CreditCard, HelpCircle, Activity } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminPanel from './AdminPanel';
import SearchGlobalBar from './SearchGlobalBar';
import UserAccountDropdown from './UserAccountDropdown';
import Logo from './Logo';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signOut, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location?.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event?.target?.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
      if (!event?.target?.closest('.admin-panel-container')) {
        setShowAdminPanel(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      setIsMobileMenuOpen(false);
      setActiveDropdown(null);
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      navigate('/');
    }
  };

  const navGroups = [
    {
      label: 'Core',
      icon: BarChart3,
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
        { name: 'Lead Management', href: '/lead-management', icon: UserPlus },
        { name: 'Lead Discovery', href: '/lead-discovery', icon: Search },
        { name: 'Communication Hub', href: '/communication-hub', icon: MessageSquare },
        { name: 'Calendar', href: '/calendar-scheduling', icon: Calendar },
        { name: 'Analytics', href: '/analytics-dashboard', icon: TrendingUp },
      ]
    },
    {
      label: 'AI Tools',
      icon: Brain,
      items: [
        { name: 'AI Sales Closer', href: '/ai-sales-closer', icon: Zap },
        { name: 'AI Lead Finder', href: '/ai-lead-finder-and-scrubber', icon: Search },
        { name: 'AI Person Finder', href: '/ai-person-finder', icon: Users },
        { name: 'AI Dialer', href: '/ai-dialer', icon: Phone },
        { name: 'AI Template Studio', href: '/ai-template-studio', icon: FileText },
        { name: 'AI Marketing Funnels', href: '/ai-build-marketing-funnels', icon: Bot },
        { name: 'AI Lead Scoring', href: '/ai-lead-scoring-system', icon: TrendingUp },
      ]
    },
    {
      label: 'Workflows',
      icon: Workflow,
      items: [
        { name: 'Workflow Management', href: '/workflow-management', icon: Workflow },
        { name: 'Workflow Builder', href: '/workflow-builder', icon: Zap },
        { name: 'Automation Designer', href: '/automation-workflow-designer', icon: Zap },
        { name: 'Funnel Management', href: '/funnel-management', icon: BarChart3 },
        { name: 'Funnel Builder', href: '/funnel-builder', icon: BarChart3 },
        { name: 'Sales Funnel', href: '/sales-funnel-management', icon: TrendingUp },
        { name: 'Funnel Analytics', href: '/funnel-analytics', icon: BarChart3 },
        { name: 'ROI Tracking', href: '/roi-tracking-dashboard', icon: TrendingUp },
      ]
    },
    {
      label: 'Integrations',
      icon: Link2,
      items: [
        { name: 'GoHighLevel', href: '/gohighlevel-integration', icon: Globe },
        { name: 'Integration Hub', href: '/cross-platform-integration-hub', icon: Link2 },
        { name: 'Integration Analytics', href: '/integration-analytics-monitoring', icon: Activity },
        { name: 'API Keys', href: '/api-keys-management', icon: Settings },
        { name: 'System Diagnostics', href: '/system-diagnostics', icon: Activity },
      ]
    },
    {
      label: 'Account',
      icon: User,
      items: [
        { name: 'Profile Settings', href: '/profile-settings', icon: User },
        { name: 'Subscription', href: '/subscription-management', icon: CreditCard },
        { name: 'Billing History', href: '/billing-history', icon: CreditCard },
        { name: 'Help & Support', href: '/help-support', icon: HelpCircle },
      ]
    }
  ];

  const isActivePath = (path) => location?.pathname === path;
  const isActiveGroup = (group) => group?.items?.some(item => isActivePath(item?.href));

  if (!user) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo size="small" variant="header" showText={true} linkTo="/" />
            <div className="flex items-center space-x-3">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-md text-sm font-medium transition-colors">
                Sign In
              </Link>
              <Link to="/register" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto">
        {/* Main Header Row */}
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Left: Mobile Menu + Logo */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none transition-colors"
              aria-label="Toggle navigation menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Logo size="small" variant="header" showText={true} linkTo="/dashboard" />
          </div>

          {/* Center: Desktop Nav Groups */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navGroups?.map((group) => {
              const GroupIcon = group?.icon;
              const isActive = isActiveGroup(group);
              return (
                <div key={group?.label} className="dropdown-container relative">
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === group?.label ? null : group?.label)}
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <GroupIcon className="w-4 h-4" />
                    <span>{group?.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${
                      activeDropdown === group?.label ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {activeDropdown === group?.label && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-1">
                      {group?.items?.map((item) => {
                        const ItemIcon = item?.icon;
                        return (
                          <Link
                            key={item?.href}
                            to={item?.href}
                            className={`flex items-center space-x-2.5 px-4 py-2.5 text-sm transition-colors ${
                              isActivePath(item?.href)
                                ? 'bg-blue-50 text-blue-700 font-medium' :'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <ItemIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{item?.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Right: Search + User */}
          <div className="flex items-center space-x-2">
            <div className="hidden xl:block w-48">
              <SearchGlobalBar />
            </div>

            {/* Admin Badge */}
            {isAdmin && (
              <div className="admin-panel-container relative">
                <button
                  onClick={() => setShowAdminPanel(!showAdminPanel)}
                  className="flex items-center px-2 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-full hover:bg-red-100 border border-red-200 transition-colors"
                >
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </button>
                {showAdminPanel && (
                  <div className="absolute right-0 top-full mt-2 w-80 z-[60]">
                    <AdminPanel />
                  </div>
                )}
              </div>
            )}

            <UserAccountDropdown />
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-3 space-y-1">
              {navGroups?.map((group) => (
                <div key={group?.label}>
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {group?.label}
                  </div>
                  {group?.items?.map((item) => {
                    const ItemIcon = item?.icon;
                    return (
                      <Link
                        key={item?.href}
                        to={item?.href}
                        className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                          isActivePath(item?.href)
                            ? 'bg-blue-50 text-blue-700 font-medium' :'text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <ItemIcon className="w-4 h-4 flex-shrink-0" />
                        <span>{item?.name}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <button
                  onClick={handleSignOut}
                  className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;